import { useCallback } from "react";
import {
  clearAllListParams,
  clearListParamsShowAll,
} from "./list-url-filters";

export type DeskListFilterResetActions = {
  canResetToDefault: boolean;
  canClearAll: boolean;
  onResetToDefault: () => void;
  onClearAll: () => void;
};

/**
 * Reset / clear desk list URL filters (+ local search box) — plan/30+35.
 * Pair with `<DeskListFilterReset {...actions} />`.
 */
export function useDeskListFilterReset(opts: {
  patchParams: (mutate: (params: URLSearchParams) => void) => void;
  /** Local search field state (URL `q` is cleared via patchParams). */
  setQuery?: (next: string) => void;
  /** Away from list defaults (e.g. SO not on clean open). */
  canResetToDefault: boolean;
  /** Not fully unrestricted (Clear all still has something to remove). */
  canClearAll: boolean;
  /**
   * After wipe, set `lifecycle=all` so Clear all is wider than Reset to default
   * (SO/PO default open). Omit when empty URL already means “show all”.
   */
  clearAllSetsLifecycleAll?: boolean;
}): DeskListFilterResetActions {
  const {
    patchParams,
    setQuery,
    canResetToDefault,
    canClearAll,
    clearAllSetsLifecycleAll,
  } = opts;

  const onResetToDefault = useCallback(() => {
    setQuery?.("");
    patchParams(clearAllListParams);
  }, [patchParams, setQuery]);

  const onClearAll = useCallback(() => {
    setQuery?.("");
    patchParams((p) => {
      if (clearAllSetsLifecycleAll) {
        clearListParamsShowAll(p, { lifecycleDefault: "open" });
      } else {
        clearAllListParams(p);
      }
    });
  }, [patchParams, setQuery, clearAllSetsLifecycleAll]);

  return {
    canResetToDefault,
    canClearAll,
    onResetToDefault,
    onClearAll,
  };
}
